'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  hasTrackingConsent,
  isConsentDecided,
  isConsentRequired,
  onOpenConsentSettings,
  writeConsent,
} from '../../lib/consent'

// EU/EEA/UK cookie-consent banner (MVP). Shown on first visit only to
// consent-required visitors who haven't chosen yet; non-EU visitors never see it
// (they are implied-accept at startup). The footer "Cookie settings" link can
// re-open it for anyone, which is also the withdrawal path.
export default function ConsentBanner() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const bannerRef = useRef(null)

  useEffect(() => {
    if (isConsentRequired() && !isConsentDecided()) setVisible(true)
    return onOpenConsentSettings(() => setVisible(true))
  }, [])

  // Publish the banner height as a global CSS var so bottom-anchored UI (the
  // /generate prompt bar, mobile nav, etc.) can lift above it instead of being
  // covered. Tracks reflows (e.g. the message wrapping to two rows on mobile)
  // and clears back to 0 whenever the banner is hidden or unmounts.
  useLayoutEffect(() => {
    const root = document.documentElement
    if (!visible || !bannerRef.current) {
      root.style.setProperty('--consent-banner-height', '0px')
      return
    }
    const el = bannerRef.current
    const apply = () =>
      root.style.setProperty('--consent-banner-height', `${el.offsetHeight}px`)
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.setProperty('--consent-banner-height', '0px')
    }
  }, [visible])

  const accept = () => {
    writeConsent(true)
    setVisible(false)
  }

  const reject = () => {
    const wasAllowed = hasTrackingConsent()
    writeConsent(false)
    setVisible(false)
    // If trackers had already loaded (the visitor is withdrawing consent),
    // reload so the GA/Ads scripts are not left running for the rest of the page.
    if (wasAllowed && typeof window !== 'undefined') window.location.reload()
  }

  if (!visible) return null

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label={t('cookieConsent.title', 'Cookie notice')}
      // Deliberately compact: this sits over the bottom of every page for
      // first-time EU visitors, and the /generate prompt bar lifts by exactly
      // its height. The copy stays as-is (it's translated into all 10 locales,
      // and the second sentence is the part that makes the choice explicit) —
      // the size comes out of type scale and padding instead.
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-white/10 bg-[--background-bg-color] px-3 py-2.5 text-xs text-gray-200 md:px-4 md:py-3"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
        <p className="leading-tight">
          {t(
            'cookieConsent.message',
            'We use cookies to improve your experience and analyze how our site is used. You can accept or reject non-essential cookies.',
          )}{' '}
          <a href="/privacypolicy" className="whitespace-nowrap underline">
            {t('cookieConsent.learnMore', 'Privacy Policy')}
          </a>
        </p>
        {/* Equal size and weight on both — GDPR wants rejecting to be no harder
            than accepting, so neither shrinks below the other. */}
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={reject}
            className="flex-1 whitespace-nowrap rounded-full border border-white/40 px-3 py-1.5 font-semibold text-white md:flex-none"
          >
            {t('cookieConsent.reject', 'Reject all')}
          </button>
          <button
            type="button"
            onClick={accept}
            className="flex-1 whitespace-nowrap rounded-full bg-[--main-bg-color] px-3 py-1.5 font-semibold text-white md:flex-none"
          >
            {t('cookieConsent.accept', 'Accept all')}
          </button>
        </div>
      </div>
    </div>
  )
}
